(ns practitest.frontend.components2.search-box
  (:require [ajax.core :as ajax]
            [clojure.string :as str]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common-entities.utils :refer [entity-icon]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.overflow :as overflow]
            [practitest.frontend.components.overlay2 :as overlay2]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.common.utils :refer [development-only register-components!]]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.common.keyboard-shortcuts :as keyboard-shortcuts]
            [goog.functions :as gfuncs]))


;; Constants
;;-------------------------------------
(def min-search-string-length 1)
(def max-results 10)
(def debounce-interval 200)
(def sections-order [:requirement
                     :test
                     :testset
                     :issue])

;; Helpers
;;-------------------------------------

(defn- get-section-title [type]
  (case type
    ;; TODO: translation ? Should be somewhere already ?
    :requirement
    "Requirements"

    :test
    "Tests"

    :issue
    "Issues"

    :testset
    "TestSets"

    ;;else
    (str "Unknown " type)))

(defn- entity-key->type [entity-key]
  (case entity-key
    :qtest_design
    :test

    :requirement
    :requirement

    :suite
    :testset

    :issue
    :issue

    ;;else
    entity-key))

(defn- convert-search-data
  [data]
  (let [results (get-in data [:results :results])]
    {:total-count (get-in data [:results :total])
     :entities (for [result results]
                 (let [entity-key (first (keys (:entity result)))]
                   {:url (:url result)
                    ;; Ignore highlighting information for now
                    :fields-detected (set (keys (:matches result)))
                    :score (:score result)
                    :type (entity-key->type entity-key)
                    :id (get-in result [:entity entity-key :id])
                    :display_id (get-in result [:entity entity-key :display_id])
                    ;; Sometimes it's name and sometimes it's title
                    :name (or (get-in result [:entity entity-key :name])
                              (get-in result [:entity entity-key :title]))}))}))

(defn- should-search?
  [search-string]
  (and search-string
       (>= (count search-string) min-search-string-length)))

;; TODO: slightly annoying that a first call is debounced as well - implement something more sophisticated
(def search-data-debounced
  (gfuncs/debounce
    (fn [text]
      (re-frame/dispatch [::search-data text]))
    debounce-interval))

;; Components
;;-------------------------------------
(defn ^{:component? true} search-textbox
  "Search textbox to be reused where applicable"
  [{:keys [on-focus
           on-click
           on-change
           on-key-down
           on-clear-click
           loading?
           placeholder
           value
           class
           tab-index
           ref
           expanded?]}]
  (let [show-clear-icon? (and on-clear-click
                              (not (str/blank? value)))]
    [:div.search-textbox
     {:class class}
     [:input.search-input.mux-input
      {
       :data-testid "search-input"
       :placeholder placeholder
       :class (dom/classes-pairs-v :expanded expanded?
                                   :clearable show-clear-icon?)
       :ref (dom/ref-handler ref)
       :auto-complete "off"
       :type "text"
       :tab-index tab-index
       :on-focus on-focus
       :on-click on-click
       :on-key-down on-key-down
       :value value
       :on-change on-change}]
     [:span.search-icon
      [icons/mux-search]]

     (when loading?
       [:span.input-spinner
        [spinners/mux-spinner {:size :small
                               :fade-in? true}]])

     (when show-clear-icon?
       [:span.input-clear-value
        {:on-click (fn [e]
                     (on-clear-click e))}
        [icons/mux-x]])]))

(def description-map {:name "Title"
                      :description "Description"
                      :comments "Comments"
                      :step.description "Step Description"
                      :step.expected_results "Step Expected Results"
                      :step.name "Step Name"
                      :display_id "ID"
                      :charter "Charter"
                      :guide_points "Guide points"})

(defn- get-field-description
  [field]
  (or (get description-map field)
      "Other"))

(defn format-search-found-in [matches]
  ;; In case it's nil - we don't support "Text Found In" feature on backend side - don't display anything
  (when-not (nil? matches)
    (if (seq matches)
      (str/join ", " (map get-field-description matches))
      ;; Special case for empty collection - display as "Other" - after discussion with Yafit
      (get-field-description ::other))))

;; TODO: .entity-type-icon component ?
(defn- search-results-item
  [{:keys [type
           name
           url
           display_id
           fields-detected]
    :as item}
   cancel-fn]
  [:div.search-results-item
   [:span.icon.search-results-icon (entity-icon type)]
   [:div.search-result-item-body
    (let [title (str display_id " - " name)]
      [overflow/overflow-with-mux-tooltip
       {}
       (fn [props]
         [:a.search-result-link
          (assoc props :href url
                       :on-click (fn [e]
                                   (mixpanel/track "Search" {"Action" "Select Result in Dropdown"})

                                   ;; Need to close search screen when result is selected
                                   (cancel-fn)))
          title])
       title])

    (when-let [text (format-search-found-in fields-detected)]
      [:span.search-text-found-in
       (format-translation (get-translation "search.text-found-in.result-string" "Text found in: %{text}")
                           {:text text})])]])

(defn- search-dropdown-body
  [search-results search-url cancel-fn]
  [:div.search-results-dropdown
   (let [grouped-data (->> (:entities search-results)
                           (take max-results)
                           (group-by :type))
         entities-by-type (->> sections-order
                               (remove (fn [section] (empty? (get grouped-data section))))
                               (map (fn [section] [section (get grouped-data section)])))]
     (for [[section-type data] entities-by-type]
       [:div.search-results-section
        {:key (str section-type)}
        [:div.search-results-section-header
         (get-section-title section-type)]

        ;; enforce sorting once again (technically not required, but to keep everything stable)
        (for [item (->> data
                        (sort-by :score)
                        (reverse))]
          (with-meta
            [search-results-item item cancel-fn]
            {:key (:id item)}))]))

   [:hr.search-box-separator]

   [:div.search-results-footer
    [:div.search-results-totals
     (str "Total Results Found: " (or (:total-count search-results)
                                      0))]
    [:span
     "Use "
     [:a {:href search-url
          :on-click (fn [e]
                      (mixpanel/track "Search" {"Action" "Advanced Search in Dropdown"}))}
      "Advanced Search"]
     " to view all results / advanced search options"]]])


(defn search-box-component
  [options search-box-ref]
  (reagent/with-let [local-state (reagent/atom {:search-text ""})

                     hide! #(swap! local-state assoc :show? false)
                     show! #(swap! local-state assoc :show? true)

                     textbox-ref (or search-box-ref (atom nil))
                     cancel-fn (fn []
                                 (re-frame/dispatch [::clear-results])
                                 (swap! local-state assoc :search-text "")
                                 (hide!)
                                 ;; Remove focus from input field on Esc / Clear button press
                                 (when-let [node @textbox-ref]
                                   (.blur node)))]
    (let [show? (:show? @local-state)
          search-text (:search-text @local-state)
          results-data @(re-frame/subscribe [::data])
          current-project @(re-frame/subscribe [::subs/current-project])
          loading? @(re-frame/subscribe [::subs/progress? ::search-data])

          search-url-fn (fn []
                          (broutes/get-url "search#index" {:project_id (:id current-project)} {:query search-text}))]
      [overlay2/floating-ui-overlay
       {:placement "bottom"
        :target-width? true

        :show? show?
        :on-request-close hide!}

       ;; target
       [search-textbox
        {:on-focus (fn []
                     (show!)
                     (mixpanel/track "Search" {"Action" "Input Focus Search Box Main"})
                     (when (should-search? search-text)
                       (re-frame/dispatch [::search-data search-text])))
         :loading? loading?
         :ref textbox-ref
         :placeholder (get-translation "navigation.header-bar.search-placeholder" "Search Items by Text / ID")
         :class (dom/classes-pairs-v
                 :global-search true
                 :search-navigation2 true)
         :on-clear-click (fn [e]
                           (mixpanel/track "Search" {"Action" "Input Clear Search Box Main"})
                           (cancel-fn))
         :on-click (fn [e]
                     (mixpanel/track "Search" {"Action" "Input Click Search Box Main"}))
         :expanded? show?
         :value search-text
         :on-key-down (fn [e]
                        (let [key (.-key e)]
                          (cond
                            ;; Close on Esc
                            (= "Escape" key)
                            (cancel-fn)

                            ;; Search on Enter
                            (= "Enter" key)
                            (when (should-search? search-text)
                              (mixpanel/track "Search" {"Action" "Enter Pressed Search Box Main"})
                              ;; rebuild url string here, otherwise it will be slightly out of
                              ;; date if user quickly types in stuff and presses enter
                              (dom/navigate! (search-url-fn))))))
         :on-change (fn [e]
                      (mixpanel/track "Search" {"Action" "Input Text Change Search Box Main"})
                      (let [string (dom/event-value e)]
                        ;; Can happen if pressed Esc - dropdown will be hidden, but focus stays in the field
                        (when (and (not show?)
                                   (not (str/blank? string)))
                          (show!))

                        (swap! local-state assoc :search-text string)
                        (if (should-search? string)
                          (search-data-debounced string)
                          (re-frame/dispatch [::clear-results]))))}]

       ;; body
       [search-dropdown-body
        results-data
        (search-url-fn)
        cancel-fn]])))


;; Re-frame wiring
;;-------------------------------------
(re-frame/reg-sub
  ::state
  (fn [db]
    (::state db)))

(re-frame/reg-sub
  ::data
  :<- [::state]
  (fn [state]
    (convert-search-data (:data state))))

(defn- abort-pending-request!
  [db]
  (when-let [pending-request (get-in db [::state :pending-request])]
    (ajax/abort pending-request)))

(re-frame/reg-event-db
  ::clear-results
  (fn [db]
    (abort-pending-request! db)

    (-> db
        (dissoc ::state)
        ;; need to clear progress indicator as well
        (update :progress disj ::search-data))))

;; TODO: generic handler
(re-frame/reg-event-fx
  ::search-error
  (fn [{db :db} [_ progress-item message response]]
    ;; Do not handle if aborted by us
    (when-not (= :aborted (:failure response))
      {:db (-> db
               (update :progress disj progress-item)
               (update ::state dissoc :pending-request))
       :dispatch [::notifications/error2 message]})))

(re-frame/reg-event-db
  ::pending
  (fn [db [_ xhrio]]
    (assoc-in db [::state :pending-request] xhrio)))

(re-frame/reg-event-db
  ::got-results
  (fn [db [_ response]]
    (-> db
        (assoc-in [::state :data] response)
        (update :progress disj ::search-data)
        (update ::state dissoc :pending-request))))

(re-frame/reg-event-fx
  ::search-data
  (fn [{db :db} [_ text]]
    (let [current-project (subs/get-current-project db)]
      ;; abort concurrent requests
      (abort-pending-request! db)
      {:db (update db :progress conj ::search-data)
       :http-xhrio
       {:method :get
        :uri (broutes/get-url "search#index" {:project_id (:id current-project)})
        :params {:query text
                 :per_page max-results}
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success [::got-results]
        :on-request [::pending]
        :on-failure [::search-error ::search-data "Error fetching search results"]}})))

(comment
  (tap> (re-frame/subscribe [::state]))
  (tap> (re-frame/subscribe [::data])))

(development-only
  (defonce examples-state (reagent/atom {:search-string ""}))

  (defn ^{:component-example search-textbox}
    search-textbox-examples
    []
    [:div
     [:h4 "Search textbox examples"]

     [:div {:style {:width "400px"}}
      [:p "Simple search textbox without placeholder"]
      [search-textbox {:value (:search-string @examples-state)
                       :on-change #(swap! examples-state assoc :search-string (dom/event-value %))}]

      [:p "Simple search textbox with placeholder and clearable"]
      [search-textbox {:value (:search-string @examples-state)
                       :placeholder "Search for something interesting"
                       :on-change #(swap! examples-state assoc :search-string (dom/event-value %))
                       :on-clear-click #(swap! examples-state assoc :search-string "")}]]])
  )

(register-components!)
